/**
 * Pure V1/V2 module-version decision logic (Workstream E phase 2).
 *
 * Kept free of Angular imports so the menu/default-route selection can be
 * unit-tested in isolation. `ModuleVersionService` (module-version.service.ts)
 * owns the state; `MenuService.setMenuITEMS` and the post-login/Home-key
 * navigation call into these helpers.
 *
 * Owner directive (2026-07-11): each version shows ONLY its own screens -
 * the Settings > Module Versions toggle is the switch, there are no more
 * cross-version "(Classic)"/"(v2)" escape hatches in the menu. Routes/guards
 * stay registered under both versions (rollback safety at the URL level).
 */

export type ModuleVersion = 'V1' | 'V2';

/** Module keys understood by the backend `v2/module-version` API. */
export const VAZHIPADU_MODULE = 'VAZHIPADU';

/** Absolute booking-screen routes (post-login / Home key / dashboard tile). */
export const BOOKING_ROUTE_V2 = '/app/pooja-v2';
export const BOOKING_ROUTE_V1 = '/app/pooja/pooja-booking-stoda';

/** Default booking-screen route for a VAZHIPADU module version. */
export function vazhipaduBookingRoute(version: ModuleVersion): string {
    return version === 'V1' ? BOOKING_ROUTE_V1 : BOOKING_ROUTE_V2;
}

/**
 * Minimal structural view of a menu entry - matches `MenuItem`
 * (core/services/menu.service.ts) without importing it, so this file has no
 * dependency cycle with the menu code.
 */
export interface MenuEntryLike {
    id: string;
    text: string;
    route: string | null;
    submenu?: MenuEntryLike[] | null;
}

/** Menu ids rewired/stripped by the toggle (single source of truth). */
export const VAZHIPADU_MENU_IDS = {
    booking: 'vazhipad',
    /** Classic rollback entry - stripped in BOTH versions (V1 rewires the primary instead). */
    bookingClassic: 'vazhipad-classic',
    head: 'vazhipad-head',
    /** Classic rollback entry - stripped in BOTH versions. */
    headClassic: 'vazhipad-head-classic'
} as const;

/**
 * The single global V1/V2 switch drives EVERY module's menu entry (owner
 * directive 2026-07-12: "when choose v2 ... all v2 pages shown; if v1, only old
 * pages"). Each mapped menu id has its route rewired to the v2 screen on V2 (the
 * default) and to the legacy screen on V1. Adding a module = one line here.
 * Both routes stay registered in app-routing.module.ts (URL-level rollback).
 */
export const MODULE_ROUTE_MAP: Record<string, { v1: string; v2: string }> = {
    // Vazhipadu (booking + pooja-master head)
    'vazhipad':           { v2: 'pooja-v2',        v1: 'pooja/pooja-booking-stoda' },
    'vazhipad-head':      { v2: 'vazhipadu-v2',    v1: 'stoda/add-pooja' },
    // The six WS-G operational modules + Income (WS-H)
    'expense':            { v2: 'expense-v2',      v1: 'stoda/expense-billing-stoda' },
    'income':             { v2: 'income-v2',       v1: 'stoda/income-billing' },
    'sale':               { v2: 'pooja-stall-v2',  v1: 'stoda/sale' },
    // Members entry (owner directive 2026-07-23): the former Transaction ->
    // Membership ('membership-billing') menu entry is REMOVED — Administration
    // -> Members ('member-heads') is the one member entry form. Its v1 route is
    // 'membership/add-member-on' (same AddMemberComponent as 'add-member';
    // add-member-on is the admin variant carrying the 'Members' page title).
    'member-heads':       { v2: 'membership-v2',   v1: 'membership/add-member-on' },
    'salary':             { v2: 'salary-v2',       v1: 'salary/salary' },
    'auditorium':         { v2: 'auditorium-v2',   v1: 'auditorium' },
    // Master data (Employee is the one modernized master; other masters have no
    // v2 yet and stay on their legacy routes — not in this map, so untouched).
    'employees':          { v2: 'employee-v2',     v1: 'employees' },
    // Enquiry head (owner directive 2026-07-23): the Vazhipadu Enquiry entry
    // follows the global toggle exactly like the booking screen.
    'vazhipadu-enquiry':  { v2: 'pooja-enquiry-v2', v1: 'stoda/pooja-enquiry' }
} as const;

/**
 * V2 screens with a classic sibling that live OUTSIDE `MODULE_ROUTE_MAP`
 * (they are not menu-toggled modules). Used only by the Alt+O shortcut.
 */
export const EXTRA_CLASSIC_ROUTE_MAP: Record<string, string> = {
    'pooja-enquiry-v2': 'stoda/pooja-enquiry'
} as const;

/**
 * Classic (V1) sibling route for a current router URL — the global Alt+O
 * "show me the classic page" shortcut. Matches when the URL is a mapped v2
 * screen (or any child route of one); returns null for classic/unmapped
 * pages so the shortcut is a no-op there.
 */
export function classicRouteForUrl(url: string): string | null {
    const path = (url || '').split('?')[0].split('#')[0];
    if (!path.startsWith('/app/')) {
        return null;
    }
    const rel = path.substring('/app/'.length);
    const candidates: Array<{ v2: string; v1: string }> = [
        ...Object.values(MODULE_ROUTE_MAP),
        ...Object.entries(EXTRA_CLASSIC_ROUTE_MAP).map(([v2, v1]) => ({ v2, v1 }))
    ];
    for (const { v2, v1 } of candidates) {
        if (rel === v2 || rel.startsWith(v2 + '/')) {
            return '/app/' + v1;
        }
    }
    return null;
}

/**
 * V2-only report menu ids - stripped entirely on V1 (their screens belong to
 * the V2 workflow). Future V2 reports: add the menu id here, nothing else.
 */
export const V2_ONLY_REPORT_MENU_IDS: readonly string[] = [
    'voucher-collection-report-v2',
    'pooja-collection-jspdf-v2',
    'pooja-bookings-jspdf-v2',
    'pooja-invoices-jspdf-v2',
    'stock-collection-jspdf-v2',
    // Voucher-backed financial reports (WS-E financial batch).
    'trial-balance-v2',
    'balance-sheet-v2',
    'income-expenditure-v2',
    'general-ledger-v2',
    'cash-book-v2',
    'bank-book-v2',
    // Corrected legacy financial statements (WS-F phase F3, Expense column
    // per D-F5 - all new page ids are V2-only).
    'income-expense-statement-v2',
    'receipt-payment-statement-v2',
    // The classified/summary Pooja report ids ('pooja-collection-classified-v2',
    // 'pooja-summary-v2', 'pooja-bookings-classified-v2',
    // 'pooja-invoices-classified-v2') were removed 2026-07-13: the
    // detail-driven Pooja Collection / Bookings / Invoices (V2) screens carry
    // every classification dimension client-side, so the separate classified
    // screens are retired.
    // Expense + Stock movement family (WS-F phase F6a; Expense column +
    // Stock submenu).
    'expense-report-v2',
    'stock-transfer-v2',
    'stock-consumption-v2',
    // Secondary report families (WS-F phase F6b; Expense column, Card/Cheque
    // in the Audit column).
    'nadavaravu-v2',
    'avakasi-vihitham-v2',
    'vendor-statement-v2',
    'payment-mode-v2',
    // Postage / Receivable / Payable family (WS-F phase F6c; Postage in the
    // Expense column, Receivable + Payable in the Audit column).
    'postage-v2',
    'receivable-v2',
    'payable-v2',
    // Day Book (WS-F phase F6d; Audit column).
    'day-book-v2',
    // Partial Payment / Current Stock / Cancelled Transaction (WS-F phase F6e;
    // Partial Payment + Cancelled Transaction in the Audit column, Current Stock
    // in the Stock submenu).
    'partial-payment-v2',
    'cancelled-transaction-v2',
    'current-stock-v2',
    // Pooja report family (V2) - the 8 remaining legacy Jasper pooja reports
    // rebuilt as detail-driven jsPDF screens (owner directive 2026-07-14), all
    // in the Pooja column. 'pooja-summary-v2' is the NEW detail-driven Pooja
    // Summary (the earlier same-id classified screen was retired 2026-07-13).
    'online-collection-v2',
    'pooja-category-list-v2',
    'monthly-pooja-v2',
    'star-pooja-v2',
    'registered-devotee-v2',
    'pooja-summary-v2',
    'per-pooja-items-v2',
    'manual-entry-v2',
    // Expense-column completion (owner directive 2026-07-14) - the remaining
    // legacy Expense reports as faithful jrxml-filter jsPDF screens, each placed
    // beside its legacy sibling in the Expense column. (partial-payment-v2 +
    // cancelled-transaction-v2 above are relocated Audit->Expense, already listed.)
    'nadavaravu-revised-v2',
    'voucher-report-v2',
    'cash-book-collection-v2',
    'attendance-v2'
];

// ---------------------------------------------------------------------------
// WS-FHP — VENUE_MODULE (Auditorium vs Function Hall), owner decision D-FHP-9
// ---------------------------------------------------------------------------

/**
 * Which venue module this temple runs. STRICTLY either/or (owner, 2026-07-25):
 * there is no BOTH — the menu shows exactly one venue module. Default
 * AUDITORIUM, so every existing temple's menu is unchanged until it switches.
 */
export type VenueModule = 'AUDITORIUM' | 'FUNCTION_HALL';

/** Value in force before/without any temple config. */
export const DEFAULT_VENUE_MODULE: VenueModule = 'AUDITORIUM';

/**
 * The two Auditorium menu entries (Administration master + Transaction
 * booking) — visible only while VENUE_MODULE = AUDITORIUM.
 */
export const AUDITORIUM_VENUE_MENU_IDS: readonly string[] = [
    'add-auditorium',
    'auditorium'
] as const;

/**
 * The four Function Hall booking/enquiry entries (§6.8) — visible only while
 * VENUE_MODULE = FUNCTION_HALL.
 *
 * DELIBERATELY NOT in this list: 'function-hall-masters' and
 * 'function-hall-settings'. VENUE_MODULE itself is edited on the Function Hall
 * Settings screen, so gating that screen on the value it sets would make
 * FUNCTION_HALL unreachable from the menu on a default (AUDITORIUM) temple —
 * a bootstrap deadlock. §6.8 gates "the four booking/enquiry menu entries and
 * the two Auditorium entries", which is exactly what happens here; the two
 * setup entries stay visible and are FULL-role gated instead.
 */
export const FUNCTION_HALL_VENUE_MENU_IDS: readonly string[] = [
    'function-hall',
    'priest-appointment',
    'function-hall-enquiry',
    'priest-enquiry'
] as const;

/**
 * Applies the temple's VENUE_MODULE to a freshly built menu tree (mutates and
 * returns it, like `applyVazhipaduVersionToMenus` — `setMenuITEMS` always
 * builds a new `Menus()`), stripping whichever venue module is NOT in force.
 *
 * Routes for both modules stay registered in app-routing.module.ts, so a
 * hidden screen is still reachable by URL (the same rollback safety the
 * V1/V2 toggle keeps).
 */
export function applyVenueModuleToMenus<T extends MenuEntryLike>(
    menus: T[], venue: VenueModule): T[] {
    const stripIds = new Set<string>(venue === 'FUNCTION_HALL'
        ? AUDITORIUM_VENUE_MENU_IDS
        : FUNCTION_HALL_VENUE_MENU_IDS);
    for (const top of menus as MenuEntryLike[]) {
        if (!top.submenu?.length) {
            continue;
        }
        top.submenu = top.submenu.filter(item => !stripIds.has(item.id));
    }
    return menus;
}

/**
 * Applies the VAZHIPADU module version to a freshly built menu tree
 * (mutates and returns it - `setMenuITEMS` always builds a new `Menus()`).
 * Each version shows ONLY its own screens (the Settings toggle is the
 * switch); routes stay registered under both versions.
 *
 * V2 (and default): "Vazhipadu" → pooja-v2 and "Vazhipadu Head" →
 * vazhipadu-v2 as authored; the "(Classic)" entries are stripped; every V2
 * report entry stays visible.
 *
 * V1: the primary entries are rewired to the classic screens (labels
 * unchanged), the "(Classic)" entries are stripped (they would duplicate the
 * rewired primaries), and ALL V2-only report entries are stripped.
 */
export function applyVazhipaduVersionToMenus<T extends MenuEntryLike>(
    menus: T[], version: ModuleVersion): T[] {
    const isV1 = version === 'V1';
    const stripIds = new Set<string>([
        VAZHIPADU_MENU_IDS.bookingClassic,
        VAZHIPADU_MENU_IDS.headClassic,
        ...(isV1 ? V2_ONLY_REPORT_MENU_IDS : [])
    ]);
    for (const top of menus as MenuEntryLike[]) {
        if (!top.submenu?.length) {
            continue;
        }
        // Rewire every mapped module entry to its v1/v2 route for this version.
        for (const item of top.submenu) {
            const routes = MODULE_ROUTE_MAP[item.id];
            if (routes) {
                item.route = isV1 ? routes.v1 : routes.v2;
            }
        }
        top.submenu = top.submenu.filter(item => !stripIds.has(item.id));
    }
    return menus;
}
